import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { PageTitle } from '@/components/PageTitle';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { requirePermission } from '@/lib/guards';
import { orpc } from '@/lib/orpc';

export const Route = createFileRoute('/_app/linkedin/campaigns')({
  validateSearch: z.object({ account: z.number().int().optional() }),
  beforeLoad: ({ context }) => requirePermission(context.me, 'linkedin.view'),
  component: CampaignsPage
});

const STATUS_BADGE: Record<string, string> = { ACTIVE: 'badge-success', PAUSED: 'badge-warning', COMPLETED: 'badge-ghost', DRAFT: 'badge-ghost' };

function CampaignsPage() {
  const { t } = useTranslation();
  const { account } = Route.useSearch();
  const navigate = Route.useNavigate();
  const accounts = useQuery(orpc.linkedin.accounts.queryOptions());
  const campaigns = useQuery(orpc.linkedin.campaigns.queryOptions({ input: { limit: 500, offset: 0, ...(account && { accountId: account }) } }));

  return (
    <div className="space-y-6 p-6">
      <PageTitle
        title={t('linkedin.campaigns')}
        items={[{ label: t('linkedin.section') }, { label: t('linkedin.campaigns'), active: true }]}
        actions={
          accounts.data && accounts.data.length > 1 && (
            <select
              className="select select-sm w-56"
              aria-label={t('linkedin.account')}
              value={account ?? ''}
              onChange={(e) => void navigate({ search: { account: e.target.value ? Number(e.target.value) : undefined }, replace: true })}>
              <option value="">{t('linkedin.allAccounts')}</option>
              {accounts.data.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          )
        }
      />
      <div className="card bg-base-100 shadow-sm">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('linkedin.campaign')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('linkedin.period')}</th>
                  <th className="text-end">{t('linkedin.impressions')}</th>
                  <th className="text-end">{t('linkedin.clicks')}</th>
                  <th className="text-end">{t('linkedin.leads')}</th>
                  <th className="text-end">{t('linkedin.cost')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.data?.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-base-content/60">
                      {t('linkedin.noCampaigns')}
                    </td>
                  </tr>
                )}
                {campaigns.data?.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-base-content/60 text-xs">
                        {[c.groupName, c.objectiveType].filter(Boolean).join(' · ')}
                      </p>
                    </td>
                    <td>
                      <span className={`badge badge-soft badge-sm ${STATUS_BADGE[c.status ?? ''] ?? 'badge-ghost'}`}>{c.status ?? '–'}</span>
                    </td>
                    <td className="text-sm whitespace-nowrap">
                      {formatDate(c.runStart)} – {c.runEnd ? formatDate(c.runEnd) : '…'}
                    </td>
                    <td className="text-end tabular-nums">{formatNumber(c.impressions)}</td>
                    <td className="text-end tabular-nums">{formatNumber(c.clicks)}</td>
                    <td className="text-end tabular-nums">{formatNumber(c.leads)}</td>
                    <td className="text-end tabular-nums">{formatMoney(c.cost, c.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
